import React from 'react';
import * as mobxReact from 'mobx-react';
import { useIntl } from 'react-intl';
import AttendeesLiveFormVM, { ParticipantsLiveFormTabs } from '../attendees-live-form.viewmodel';
import { ITheadSort, ListLayout } from '../../../../../global/tables/table-list-layout';
import { SortOrder } from '../../../../../../api/shared.dto';
import { DateFnsFormats } from '../../../../../../infrastructure/date-formatter';
import * as IoC from 'typescript-ioc';
import { DateTimeFormatter } from '../../../../../../infrastructure/implementations/date-formatter';
import { isSameDay } from 'date-fns';
import { getFormattedTimezoneOffset, utcToZonedTime } from '../../../../../../infrastructure/utils/date-fns-overrides.utils';
import {
    ARRIVAL_TEXT,
    DEPARTURE_TEXT,
    ORG_NAME_TEXT,
    SEARCH_REGISTRANTS_TEXT, SEARCH_RESULT_TEXT,
} from '../attendees-live-form.texts';
import DefaultButton from '../../../../../global/default-button/default-button';
import { LOAD_MORE_BUTTON_TEXT, LOADING } from '../../../../../../constants/common-labels.constants';
import { NAME_TEXT } from '../../../../../../constants/common-labels.constants';
import { AttendeeStatusTabs } from './components/attendees-live-list-item-tabs';
import { JoinedLiveListItem, RegistrantsLiveListItem } from './components/attendees-live-list-item';
import '../attendees-live-form.scss';
import { SearchInput } from '../../../../../global/search-input/search-input';
import Text from '../../../../../global/text/text';
import { IRegistrant } from '../participants-live-form/partisipants-live-form-registrants-tab.viewmodel';
import { IAttendeeLiveSessionDTO } from '../../../../../../api/control.dao';

interface IProps {
    vm: AttendeesLiveFormVM;
}

type TGetAttendeesLiveSortKeys = 'arrival';

const AttendeesLiveFormBody = mobxReact.observer((props: IProps) => {
    const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);
    const { vm } = props;
    const intl = useIntl();
    const activeTabVM = vm.activeTab === ParticipantsLiveFormTabs.JOINED ? vm.joinedTabVM : vm.registrantsTabVM;
    const isLoading = vm.registrantsTabVM.isFetchingParticipants();

    const {
        startDateTime,
        endDateTime,
        eventTimezone,
        presenterOrganizations,
        activeTab,
    } = vm;

    const defaultTheads: ITheadSort<TGetAttendeesLiveSortKeys>[] = [
        {
            title: intl.formatMessage(NAME_TEXT),
            width: '105px'
        },
        {
            title: intl.formatMessage(ORG_NAME_TEXT),
            width: '105px'
        },
    ];

    const joinedTheads: ITheadSort<TGetAttendeesLiveSortKeys>[] = [
        ...defaultTheads,
        {
            title: intl.formatMessage(ARRIVAL_TEXT)
        },
        {
            title: intl.formatMessage(DEPARTURE_TEXT)
        },

    ];

    const startTime = dateTimeFormatter.dateToFormattedString(
        startDateTime,
        dateTimeFormatter.getLocalTimeFormat()
    );
    const endTime = dateTimeFormatter.dateToFormattedString(
        endDateTime,
        dateTimeFormatter.getLocalTimeFormat()
    );
    const dayStringInEventTimezone = dateTimeFormatter.dateToFormattedString(
        startDateTime,
        DateFnsFormats.StringifiedFullDayMonthNumericDateYear,
        eventTimezone
    );
    const startTimeInEventTimezone = dateTimeFormatter.dateToFormattedString(
        startDateTime,
        dateTimeFormatter.getLocalTimeFormat(),
        eventTimezone
    );
    const endTimeInEventTimezone = dateTimeFormatter.dateToFormattedString(
        endDateTime,
        dateTimeFormatter.getLocalTimeFormat(),
        eventTimezone
    );
    const localDayString = dateTimeFormatter.dateToFormattedString(
        startDateTime,
        DateFnsFormats.StringifiedFullDayMonthNumericDateYear,
    );
    const isEventTimezoneTimeDifferents = startTimeInEventTimezone !== startTime;
    const timeZoneOffset = getFormattedTimezoneOffset(startDateTime);

    const changeActiveTab = (tab: ParticipantsLiveFormTabs): void => {
        if (tab !== activeTab) {
            activeTabVM.pagingVM.selectPage(1);
            vm.setActiveTab(tab);
            vm.registrantsTabVM.clearSearchQuery();
        }
    };

    return (
        <div className='oe-form attendees-live-form'>
            <section>
                <span className='attendees-live-form__event-day'>
                    {dayStringInEventTimezone}
                </span>
                <div className='attendees-live-form__event-time'>
                    {startTimeInEventTimezone} - {endTimeInEventTimezone}
                </div>
                {!isSameDay(startDateTime, utcToZonedTime(startDateTime, eventTimezone)) &&
                    <div className='schedule meeting-list-item__text meeting-list-item__text--small meeting-list-item__text--date-and-time'>
                        {localDayString}
                    </div>
                }
                {isEventTimezoneTimeDifferents &&
                    <div className='schedule meeting-list-item__text meeting-list-item__text--small meeting-list-item__text--date-and-time'>
                        {startTime} - {endTime} ({timeZoneOffset})
                    </div>
                }
                <div className='attendees-live-form__organization overflow-ellipsis'>
                    {presenterOrganizations.join(', ')}
                </div>
            </section>
            <section className='events-list fixed-list-layout'>
                <div className='attendees-live-form__status-tabs'>
                    {activeTabVM.attendees && <AttendeeStatusTabs
                        joinedTotal={vm.joinedTabVM.total}
                        registrantsTotal={vm.registrantsCounter}
                        activeTab={activeTab}
                        setActiveTab={changeActiveTab}
                        isMeetingsWithCounters={vm.isMeetingsWithCounters}
                    />}
                    {vm.isRegistrantsActiveTab() &&
                        <div className='attendees-live-form__search-input search-input-container'>
                            <SearchInput
                                placeholder={intl.formatMessage(SEARCH_REGISTRANTS_TEXT)}
                                resetSearch={vm.registrantsTabVM.clearSearchQuery}
                                onChange={(e, value) => vm.registrantsTabVM.setSearchQuery(value)}
                                withDebounce
                                withResetSearchButton
                            />
                        </div>
                    }
                </div>
                <div className='attendees-live-form__table m-bottom-12px'>
                    <ListLayout sortOptions={{
                        theads: activeTab === ParticipantsLiveFormTabs.JOINED ? joinedTheads : defaultTheads,
                        sortOrder: SortOrder.Asc,
                        sortField: 'arrival',
                    }}
                    >
                        {vm.isRegistrantsActiveTab() && vm.registrantsTabVM.isSearchQueryValid() && !isLoading &&
                            <tr>
                                <td colSpan={2} className='attendees-live-form__search-counter'>
                                    <Text message={intl.formatMessage(SEARCH_RESULT_TEXT, { resultCount: vm.registrantsTabVM.total })} withEllipsis/>
                                </td>
                            </tr>
                        }
                        {activeTabVM.attendees && activeTabVM.attendees
                            .slice()
                            .sort((a, b) => b.joinTime?.getTime() - a.joinTime?.getTime())
                            .map(attendee => {
                                return (
                                    activeTab === ParticipantsLiveFormTabs.JOINED
                                        ? <JoinedLiveListItem
                                            attendee={attendee as IAttendeeLiveSessionDTO}
                                            key={attendee.id}
                                            eventTimezone={eventTimezone}
                                        />
                                        : <RegistrantsLiveListItem
                                        attendee={attendee as IRegistrant}
                                        key={attendee.attendeeId}
                                        meetingID={vm.meetingID}
                                        searchQuery={vm.registrantsTabVM.searchQuery}
                                        onAttendeeEditSaveCallback={activeTabVM.onAttendeeEditSaveCallback}
                                        attendeeDetailsPopoverVM={vm.registrantsTabVM.attendeeDetailsVM}
                                        />
                                );
                            })
                        }
                    </ListLayout>
                </div>
                {activeTabVM.shouldShowLoadMoreButton && (
                    <div className='attendees-live-form__load-more'>
                        <DefaultButton
                            text={activeTabVM.fetchingParticipants
                                ? intl.formatMessage(LOADING, { suffix: '...' })
                                : intl.formatMessage(LOAD_MORE_BUTTON_TEXT)
                            }
                            onClick={() => activeTabVM.loadMoreParticipants()}
                            disabled={activeTabVM.fetchingParticipants}
                        />
                    </div>
                )}
            </section>
        </div>
    );
}
);

export { AttendeesLiveFormBody };